import axios from 'axios';
import sessionService from './sessionService';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
apiClient.interceptors.request.use(
  config => {
    const token = sessionService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const newToken = await sessionService.refreshToken();
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout user and redirect to login
        sessionService.clearSession();
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API methods
export const adminApi = {
  // Auth
  login: (credentials) => 
    apiClient.post('/admin/auth/login', credentials),
  
  verifyToken: () => 
    apiClient.get('/admin/auth/verify'),
  
  refreshToken: (refreshToken) => 
    apiClient.post('/admin/auth/refresh-token', { refreshToken }),

  // Password Management
  changePassword: (data) => 
    apiClient.post('/admin/password/change', data),
  
  // MFA
  setupMFA: () => 
    apiClient.post('/admin/mfa/setup'),
  
  verifyMFA: (token) => 
    apiClient.post('/admin/mfa/verify', { token }),
  
  disableMFA: () => 
    apiClient.post('/admin/mfa/disable'),

  // Session Management
  getCurrentSession: () => 
    apiClient.get('/admin/session/current'),
  
  terminateOtherSessions: () => 
    apiClient.post('/admin/session/terminate-others'),
  
  terminateSession: (sessionId) => 
    apiClient.post(`/admin/session/terminate/${sessionId}`)
};

export default apiClient;